import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from "@mui/material";
import { startCase } from "lodash";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Trash2 } from "lucide-react";
import { ViewColumn } from "@mui/icons-material";
import LaydownList from "../LaydownList";
import { fetchInventoryData } from "../../../atoms/logistics/LogisticsManagementAtom";
import { useAtom } from "jotai";
import ParentTable from "./ParentTable";
import axiosInstance from "../../../utils/axios";
import * as XLSX from 'xlsx-js-style';
import settingsAtom from "../../../atoms/settings/SettingsAtom";

const defaultVisible = [
  'part_name',
  'description',
  'requiredQty',
  'deliveredQty',
  'distributedQty',
  'installedQty',
  'currentQty'
];

const allColumns = [
  { key: 'part_name', label: 'Part Number' },
  { key: 'description', label: 'Part Description' },
  { key: 'requiredQty', label: 'Required Qty' },
  { key: 'deliveredQty', label: 'Delivered Qty' },
  { key: 'distributedQty', label: 'Distributed Qty' },
  { key: 'installedQty', label: 'Installed Qty' },
  { key: 'currentQty', label: 'Current Qty' },
  { key: 'damagedQty', label: 'Damaged Qty' },
  { key: 'remediateQty', label: 'Remediate Qty' },
  { key: 'DeficitQty', label: 'Deficit Qty' },
];

const childHeaders = [
  { key: "date_time", label: 'Date & Time' },
  { key: "delivery_reference", label: 'Delivery Reference' },
  { key: 'deliveredQty', label: 'Delivered Qty' },
  { key: "quantity", label: "Quantity" },
  { key: "action", label: "Action" },
  { key: "comment", label: "Comment" }
];


const exportInventoryToExcel = (inventoryData, projectInfo = {}, parentHeaders, childHeaders) => {

  const getLabel = (value) => {
    if(value === 0) {
      return "Devilery"
    } else if(value === 2) {
      return "Intersite movement issued"
    } else if(value === 3) {
      return "Intersite movement received"
    } else {
      return "-"
    }
  }

  const excelData = [];
  
  if (projectInfo.projectNumber) {
    excelData.push(['Project Number', projectInfo.projectNumber, '', '', '', '', '', '', '', '']);
  }
  if (projectInfo.projectName) {
    excelData.push(['Project Name', projectInfo.projectName, '', '', '', '', '', '', '', '']);
  }
  if (projectInfo.reportTitle) {
    excelData.push(['Report', projectInfo.reportTitle, '', '', '', '', '', '', '', '']);
  }
  if (projectInfo.reportDate) {
    excelData.push(['Report Date', projectInfo.reportDate, '', '', '', '', '', '', '', '']);
  }
  
  excelData.push(['', '', '', '', '', '', '', '', '', '']);

  excelData.push(parentHeaders.map(h => h.label));

  inventoryData.forEach(item => {
    const deficitQty = (item.required_qty || 0) - (item.current_qty || 0);
    
    const parentRow = [
      item.part_name || '',
      item.description || '',
      item.required_qty || 0,
      item.delivered_qty || 0,
      item.distributed_qty || 0,
      item.installed_qty || 0,
      item.current_qty || 0,
      item.damaged || 0,
      item.remediated_quantity || 0,
      deficitQty
    ];
    excelData.push(parentRow);

    if (item.inventory_logs && item.inventory_logs.length > 0) {
      const childHeaderRow = [
        '', 
        '  ' + childHeaders[0].label, 
        childHeaders[1].label, // Delivery Reference
        childHeaders[2].label, // Delivered Qty
        childHeaders[3].label, // Quantity
        childHeaders[4].label, // Comment
        childHeaders[5].label,
        '', '', '', ''
      ];
      excelData.push(childHeaderRow);

      item.inventory_logs.forEach(log => {
        const quantity = log.issued_quantity || log.received_quantity || 0;
        const action = getLabel(log?.purpose_code)
        const childRow = [
          '', 
          '    ' + (log.time || ''), 
          log.delivery_reference || '',
          log.received_quantity || 0,
          quantity,
          action,
          log.comments || '',
          '', '', '', ''
        ];
        excelData.push(childRow);
      });

      excelData.push(['', '', '', '', '', '', '', '', '', '']);
    }
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(excelData);

  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "1976D2" } }, 
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    }
  };

  const childHeaderStyle = {
    font: { bold: true, color: { rgb: "000000" } },
    fill: { fgColor: { rgb: "E3F2FD" } }, // Light blue
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    }
  };

  const projectInfoStyle = {
    font: { bold: true, color: { rgb: "000000" } },
    fill: { fgColor: { rgb: "F5F5F5" } },
    alignment: { horizontal: "left", vertical: "center" }
  };

  const dataStyle = {
    alignment: { horizontal: "center", vertical: "center" },
    border: {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } }
    }
  };

  const range = XLSX.utils.decode_range(ws['!ref']);
  
  const tableBorderStyle = {
    border: {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } }
    },
    alignment: { horizontal: "center", vertical: "center" }
  };
  
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      
      if (!ws[cellAddress]) {
        ws[cellAddress] = { t: 's', v: '' };
      }
      
      ws[cellAddress].s = { ...tableBorderStyle };
    }
  }
  
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
      if (!ws[cellAddress]) continue;

      const cellValue = ws[cellAddress].v;
      const cellValueStr = String(cellValue || '');
      
      if (R < 4 && C === 0 && (
        cellValueStr === 'Project Number' || 
        cellValueStr === 'Project Name' || 
        cellValueStr === 'Report' || 
        cellValueStr === 'Report Date'
      )) {
        ws[cellAddress].s = {
          ...projectInfoStyle,
          border: tableBorderStyle.border
        };
      }
      else if (R < 4 && C === 1 && cellValue) {
        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: "000000" } },
          fill: { fgColor: { rgb: "F5F5F5" } },
          alignment: { horizontal: "left", vertical: "center" },
          border: tableBorderStyle.border
        };
      }
      else if (parentHeaders.some(h => h.label === cellValueStr)) {
        ws[cellAddress].s = {
          ...headerStyle,
          border: tableBorderStyle.border
        };
      }
      else if (cellValueStr && childHeaders.some(h => cellValueStr.includes(h.label))) {
        ws[cellAddress].s = {
          ...childHeaderStyle,
          border: tableBorderStyle.border
        };
      }
      else if (cellValue !== '' && cellValue != null) {
        ws[cellAddress].s = {
          ...dataStyle,
          border: tableBorderStyle.border
        };
      }
    }
  }

  const columnWidths = [
    { wch: 20 }, // Part Number
    { wch: 40 }, // Part Description
    { wch: 12 }, // Required Qty
    { wch: 12 }, // Delivered Qty
    { wch: 12 }, // Distributed Qty
    { wch: 12 }, // Installed Qty
    { wch: 12 }, // Current Qty
    { wch: 12 }, // Damaged Qty
    { wch: 12 }, // Remediate Qty
    { wch: 12 }  // Deficit Qty
  ];
  
  ws['!cols'] = columnWidths;

  XLSX.utils.book_append_sheet(wb, ws, "Inventory Report");

  const now = new Date();
  const dateStr = now.toISOString().split('T')[0];
  const filename = `Inventory_Report_${dateStr}.xlsx`;

  XLSX.writeFile(wb, filename);
};


const InventoryComponent = ({ data, title, laydownList, current, setCurrent, setLaydown, aliasName }) => {
  const [visible, setVisible] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState(defaultVisible);
  const [, setInventory] = useAtom(fetchInventoryData);
  const [loader, setLoader] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [downloadLoader, setDownloadLoader] = useState(false);

  const [settingsData] = useAtom(settingsAtom)
  const { projectList } = settingsData;
  const projectInfo = projectList.find(o => o.id === parseInt(localStorage.getItem("project_id")));

  const lastFetchedLaydownIdRef = useRef(null);

  const handleToggle = (key) => {
    setVisibleCols((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const fetchData = async (id) => {
    if (!id || id === lastFetchedLaydownIdRef.current) {
      return;
    }
  
    try {
      setLoader(true);
      await setInventory(id);
      lastFetchedLaydownIdRef.current = id;
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setLoader(false);
    }
  };
  
  useEffect(() => {
    if (laydownList?.length && !current?.id && !laydownList?.find((item) => item?.id === current.id)?.id) {
      setCurrent(laydownList[0]);
    }
  }, [laydownList, current?.id, setCurrent]);

  useEffect(() => {
    if (current?.id) {
      fetchData(current.id);
    }
    // eslint-disable-next-line
  }, [current?.id]);

  const handleDeleteLaydown = () => {
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoader(true);
      await axiosInstance.delete(`projects/laydown?laydown_id=${current?.id}`);
      await setLaydown();
    } catch (error) {
      console.error("Error deleting laydown:", error);
    } finally {
      setDeleteModalOpen(false);
      setDeleteLoader(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
  };

  const handleDownload = async () => {
    try {
      setDownloadLoader(true);
      
      if (!data || data.length === 0) {
        console.warn('No data available for export');
        return;
      }
      
      const projectDetails = {
        projectNumber: projectInfo?.project_number || 'N/A',
        projectName: projectInfo?.name || 'Project Name',
        reportTitle: current?.name || 'Inventory Report',
        reportDate: new Date().toLocaleDateString()
      };

      await new Promise(resolve => setTimeout(resolve, 300));
      
      exportInventoryToExcel(data, projectDetails, allColumns, childHeaders);
      
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    } finally {
      setDownloadLoader(false);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {loader ? (
        <Box
          sx={{
            height: "calc(100vh - 200px)",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "stretch", width: "100%" }}>
          <Box sx={{ width: visible ? "80%" : "100%", transition: "width 0.3s ease", height: "calc(100vh - 230px)" }}>
            <Box mb={1} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography fontSize={20} fontWeight={600}>
                {startCase(title)}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={downloadLoader ? <CircularProgress size={16} /> : <Download size={16} />}
                  onClick={handleDownload}
                  disabled={downloadLoader || !data || data.length === 0}
                >
                  {downloadLoader ? 'Downloading...' : 'Download'}
                </Button>
                <Button 
                  onClick={handleDeleteLaydown} 
                  disabled={!current?.id}
                  variant="outlined"
                  color="error"
                  startIcon={<Trash2 size={18} />}
                >
                  Delete
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ViewColumn />}
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{ mr: 4 }}
                >
                  Columns
                </Button>
              </Box>
              <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Box p={2} sx={{ display: 'flex', flexDirection: 'column', fontSize: "16px" }}>
                  {allColumns.map((col) => (
                    <FormControlLabel
                      key={col.key}
                      control={
                        <Checkbox
                          size={"small"}
                          checked={visibleCols.includes(col.key)}
                          onChange={() => handleToggle(col.key)}
                        />
                      }
                      label={<Box fontSize={16}>{col.label}</Box>}
                    />
                  ))}
                </Box>
              </Popover>
            </Box>

            <ParentTable
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              visibleCols={visibleCols}
              setVisibleCols={setVisibleCols}
              allColumns={allColumns}
              handleToggle={handleToggle}
              rows={data}
              laydownList={laydownList}
              current={current}
              aliasName={aliasName}
            />
          </Box>

          {visible && (
            <Box
              sx={{
                width: "20%",
                height: "calc(100vh - 260px)",
                borderLeft: "1px solid #ccc",
                paddingLeft: 2,
                transition: "width 0.3s ease",
              }}
            >
              <LaydownList
                currentLaydown={current}
                setCurrentLaydown={setCurrent}
                laydownList={laydownList}
              />
            </Box>
          )}

          <Box
            onClick={() => setVisible(!visible)}
            sx={{
              height: "24px",
              border: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "white",
              zIndex: 10,
              position: "absolute",
              top: 0,
              right: visible ? "20%" : 0,
              width: "24px",
              cursor: "pointer",
              transition: "right 0.3s ease",
            }}
          >
            {visible ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </Box>
        </Box>
      )}

      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title" fontWeight={600}>
          Confirm Delete Laydown
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            You are deleting the laydown with some current quantities and/or damaged quantities reported.
            This deletion will modify the initial delivery records accordingly.
            You may download the inventory log for your reference if required.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteLoader} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoader}
            startIcon={deleteLoader ? <CircularProgress size={16} /> : null}
          >
            {deleteLoader ? "Deleting..." : "Understood, proceed with deletion"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InventoryComponent;